import React, {useCallback, useEffect, useMemo, useState} from 'react';
import './create-account.scss';
import {
  defaultGapping,
  defaultLayouts, defaultPadding, defaultTypography, golfstatusTypography, GSButton, GSCaptchaCheckbox,
  GSForm, GSListPage, GSToggle, useFormDataValidation,
} from 'golfstatus_react_components';
import {useDispatch, useSelector} from "react-redux";
import {
  clearNotifications,
  createAccount, selectCaptchaData,
  selectCreateAccountData,
  selectLoading, selectNotifications, selectReferrer, setCaptchaData,
  setCreateAccountData, updateCreateAccountData,
} from "../reducers/appSlice";
import {getCreateAccountFormSections} from "../forms/LoginForms";
import {useTheme} from "../hooks/themeHooks";
import {Outlet, useNavigate, useSearchParams} from "react-router-dom";
import {logOut} from "../app/gs-session";
import {useNotificationAction} from "../hooks/notificationHooks";
import {FULFILLED, gsPrivacyPolicyURL, gsTermsOfServiceURL, REJECTED, UNDEFINED} from "../app/api";

const CreateAccount = () => {
  const [theme, , getStyle] = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const CaT = searchParams?.get("cat") === "true";
  const returningUser = searchParams?.get("ru") === "true";
  const firstName = searchParams?.get("first_name") ?? "";
  const lastName = searchParams?.get("last_name") ?? "";
  const email = searchParams?.get("email") ?? "";
  const phone = searchParams?.get("phone") ?? "";
  const invitationToken = searchParams?.get("invitation_token") ?? "";
  const tournamentId = searchParams?.get("signup_tournament_id") ?? null;
  const organizationId = searchParams?.get("signup_organization_id") ?? null;

  const createAccountDetails = useSelector(selectCreateAccountData);
  const loading = useSelector(selectLoading);
  const notifications = useSelector(selectNotifications);
  const referrer = useSelector(selectReferrer);
  const captchaData = useSelector(selectCaptchaData);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [captchaReady, setCaptchaReady] = useState(false);

  useEffect(() => {
    if (typeof grecaptcha !== UNDEFINED) {
      grecaptcha?.enterprise?.ready?.(() => { setCaptchaReady(true) }); // eslint-disable-line no-undef
    }
  }, []);

  const clearCaptchaData = useCallback(() => {
    setRedirecting(false);
    dispatch(setCaptchaData({}));
  }, [dispatch]);

  // Set context
  const [context, isValid] = useFormDataValidation({
    initialValid: false,
    data: createAccountDetails,
    setData: (updatedDetails) => dispatch(setCreateAccountData({ ...updatedDetails, username: updatedDetails.email })),
  });
  context.createAccountDetails = createAccountDetails;
  context.tosToggle = () => {
    return {
      description: (
        <div style={{ ...defaultLayouts.hStack, ...defaultGapping.smallGap, fontSize: golfstatusTypography.smallFont, flexWrap: "wrap" }}>
          <span>I have reviewed and accept GolfStatus'</span>
          <span style={{ ...defaultTypography.body, ...getStyle(theme.secondary), cursor: "pointer", textDecoration: "underline", textUnderlineOffset: "2px" }}
                onClick={ () => openLinkInTab(gsTermsOfServiceURL) }>Terms of Service</span>
          <span>and</span>
          <span style={{ ...defaultTypography.body, ...getStyle(theme.secondary), cursor: "pointer", textDecoration: "underline", textUnderlineOffset: "2px" }}
                onClick={ () => openLinkInTab(gsPrivacyPolicyURL) }>Privacy Policy</span>.
        </div>
      ),
      descriptionStyle: {
        opacity: 1,
      },
      value: (
        <GSToggle
          {...context.setToggleBinding?.("tos_accepted")}
          trueDescription={"Yes"}
          falseDescription={"No"}
        />
      ),
      validation: {
        isValid: () => context.getData()?.tos_accepted === true,
        invalidLabel: "You must accept the Terms of Service and Privacy Policy to create an account.",
      },
      failedValidation: context.validationFailed,
    };
  };
  context.createAccountButton = () => {
    return {
      value: <GSButton title={"Create Account"} type={"black"} isDisabled={!isFormValid()} onClick={createAccountAndLogin} isFocusable={true} />,
    };
  };
  context.showPassword = showPassword;
  context.togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  context.showConfirmPassword = showConfirmPassword;
  context.toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };
  context.getCaptcha = useMemo(() => {
    if (!captchaReady) {
      return;
    }
    return (
      <GSCaptchaCheckbox
        captchaKey={process.env.REACT_APP_CHECKBOX_CAPTCHA_KEY}
        action='SIGNUP'
        setToken={(value) => dispatch(setCaptchaData({ captcha_action: 'SIGNUP', captcha_token: value }))}
      />
    );
  }, [dispatch, captchaReady]);
  context.onSubmit = () => {
    if (isFormValid()) {
      createAccountAndLogin();
    }
  };

  // Effects
  useEffect(() => {
    if (CaT) {
      if (returningUser) {
        logOut();
        searchParams.set("ru", "false");
        navigate(`/create?${searchParams.toString()}`);
      }
    }

    const data = {
      first_name: firstName,
      last_name: lastName,
      email: email,
      phone: phone,
      invitation_token: invitationToken,
    };
    // include context for welcome email
    if (tournamentId) {
      data.signup_tournament_id = tournamentId;
    } else {
      data.signup_organization_id = organizationId;
    }
    dispatch(updateCreateAccountData(data));
  }, [dispatch, navigate, searchParams, CaT, returningUser, firstName, lastName, email, phone, invitationToken, tournamentId, organizationId]);

  // hide the reCAPTCHA badge
  useEffect(() => {
    const captchaBadge = window.document.getElementsByClassName('grecaptcha-badge');
    if (captchaBadge[0]) {
      captchaBadge[0]['style']['display'] = 'none';
    }
  }, []);

  // Notification hooks
  useNotificationAction(FULFILLED, notifications, () => window.location.replace(referrer), "createAccount");
  useNotificationAction(REJECTED, notifications, clearCaptchaData, "createAccount");

  // Methods and content
  const createAccountAndLogin = () => {
    dispatch(clearNotifications());
    setRedirecting(true);
    dispatch(createAccount({ ...createAccountDetails, captcha_data: captchaData }));
  };

  const isFormValid = () => {
    return isValid && Boolean(captchaData?.captcha_token);
  };

  const getTitle = () => {
    return (
      <div style={{ ...defaultLayouts.vStack, ...defaultGapping.mediumGap }}>
        <div style={{ ...defaultTypography.displaySmall }}>Create Account</div>
        <div style={{ ...defaultLayouts.hStack, ...defaultGapping.smallGap, flexWrap: "wrap" }}>
          <span style={{ ...defaultTypography.tertiary, fontWeight: golfstatusTypography.regularFontWeight }}>Already have a GolfStatus account? </span>
          <span style={{ ...defaultTypography.body, ...getStyle(theme.secondary), cursor: "pointer", textDecoration: "underline", textUnderlineOffset: "2px" }} onClick={() => goToSignIn()}>Sign In.</span>
        </div>
      </div>
    );
  };

  const goToSignIn = () => {
    dispatch(clearNotifications({ excludeAnnouncements: true }));
    navigate("/");
  };

  const openLinkInTab = (url) => {
    window.open(url, "_blank");
  };

  const getRouter = () => {
    return <Outlet />;
  };

  const isLoading = () => {
    return loading.length > 0 || redirecting;
  };

  const getContent = () => {
    return (
      <GSForm
        style={{ ...defaultPadding.xLargePad }}
        formTitle={ getTitle() }
        formSections={ getCreateAccountFormSections(context) }
      ></GSForm>
    );
  };

  const getCreateAccountPage = () => {
    return {
      getItemList: getContent,
      getRouter: getRouter,
      headerStyle: { ...defaultLayouts.hidden },
      style: { maxWidth: "480px", ...defaultLayouts.fullWidth },
      loading: isLoading(),
      loadingMainText: "Creating user...",
      loadingSubText: "Please wait...",
      alignment: "center",
    };
  };

  return (
    <create-account style={{...defaultLayouts.fullWidth, ...defaultPadding.xxLargePad, ...defaultLayouts.vStack.align("center", "flex-start")}}>
      <GSListPage { ...getCreateAccountPage() } />
    </create-account>
  );
};

export default CreateAccount;
